import { useState } from "react";
import { useTranslation } from "react-i18next";
import Button from "../../../components/base/Button";
import SEO from "../../../components/base/SEO";
import Footer from "../../../components/feature/Footer";
import Header from "../../../components/feature/Header";

const moduleColors = {
  teal: {
    chip: "bg-teal-100 text-teal-700",
    border: "border-teal-200",
    ring: "ring-teal-500",
    panel: "from-teal-500 to-cyan-500",
    soft: "bg-teal-50",
    text: "text-teal-700",
    button: "border-teal-300 hover:border-teal-500 hover:bg-teal-50",
  },
  amber: {
    chip: "bg-amber-100 text-amber-700",
    border: "border-amber-200",
    ring: "ring-amber-500",
    panel: "from-amber-500 to-orange-500",
    soft: "bg-amber-50",
    text: "text-amber-700",
    button: "border-amber-300 hover:border-amber-500 hover:bg-amber-50",
  },
  sky: {
    chip: "bg-sky-100 text-sky-700",
    border: "border-sky-200",
    ring: "ring-sky-500",
    panel: "from-sky-500 to-blue-500",
    soft: "bg-sky-50",
    text: "text-sky-700",
    button: "border-sky-300 hover:border-sky-500 hover:bg-sky-50",
  },
  rose: {
    chip: "bg-rose-100 text-rose-700",
    border: "border-rose-200",
    ring: "ring-rose-500",
    panel: "from-rose-500 to-red-500",
    soft: "bg-rose-50",
    text: "text-rose-700",
    button: "border-rose-300 hover:border-rose-500 hover:bg-rose-50",
  },
  violet: {
    chip: "bg-violet-100 text-violet-700",
    border: "border-violet-200",
    ring: "ring-violet-500",
    panel: "from-violet-500 to-fuchsia-500",
    soft: "bg-violet-50",
    text: "text-violet-700",
    button: "border-violet-300 hover:border-violet-500 hover:bg-violet-50",
  },
  slate: {
    chip: "bg-slate-100 text-slate-700",
    border: "border-slate-200",
    ring: "ring-slate-500",
    panel: "from-slate-600 to-slate-800",
    soft: "bg-slate-50",
    text: "text-slate-700",
    button: "border-slate-300 hover:border-slate-500 hover:bg-slate-50",
  },
} as const;

type EcommerceModule = {
  id: string;
  label: string;
  short: string;
  title: string;
  description: string;
  bullets: string[];
  outcomes: string[];
  icon: string;
  image: string;
  accent: keyof typeof moduleColors;
  tag: string;
};

const ecommerceModules: readonly EcommerceModule[] = [
  {
    id: "channel-sync",
    label: "商品與渠道同步",
    short: "Shopify、HKTVmall、自家網店資料一致",
    title: "把多個網店後台，變成同一套倉務語言",
    description:
      "AUTOCHAIN 先解決最根本的問題：產品資料、SKU、圖片、變體、渠道設定不能各自為政。當前線執貨同事看到的資料與網店一致，後面的揀貨、盤點、出貨才有機會穩定。",
    bullets: [
      "自動同步 Shopify、HKTVmall、SHOPLINE 與自家網站商品資料",
      "統一 SKU / barcode / variant 結構，減少人手對照",
      "渠道層級保留不同售價、上架狀態與庫存邏輯",
    ],
    outcomes: ["減少主檔錯誤", "新商品上架更快", "前線資料一致"],
    icon: "ri-links-line",
    image: "/ecommerce-flow-1-product-sync.png",
    accent: "teal",
    tag: "Step 1",
  },
  {
    id: "inbound-allocation",
    label: "入庫與分配",
    short: "入庫時已決定每個渠道可以賣幾多",
    title: "入庫後即做渠道分貨，不再靠 Excel 臨時改數",
    description:
      "貨入倉後，系統可按渠道、倉位、庫存安全量與優先級，自動分配可售數量。你不需要等到爆單才手動拆貨，亦可預留線下門市或 VIP 渠道庫存。",
    bullets: [
      "支援百分比分貨、固定數量預留與安全庫存設定",
      "根據貨位與倉容提供上架建議，減少入庫後再搬位",
      "同步記錄批次、有效期與入庫來源，方便後續追蹤",
    ],
    outcomes: ["渠道分貨清晰", "減少缺貨超賣", "入庫流程標準化"],
    icon: "ri-inbox-archive-line",
    image: "/ecommerce-flow-2-inbound.png",
    accent: "amber",
    tag: "Step 2",
  },
  {
    id: "inventory-control",
    label: "跨平台庫存聯動",
    short: "任何一邊出貨，其餘平台即時扣減",
    title: "真正做到多網店同賣，不用擔心跨平台超賣",
    description:
      "當某個渠道產生訂單並確認出貨，其他渠道的可售庫存會即時扣減。AUTOCHAIN 把『庫存準確』從倉庫內部，延伸到整個銷售網絡。",
    bullets: [
      "跨平台即時同步可售庫存與預留量",
      "可按倉庫、渠道、商品組合設置不同同步規則",
      "對接退款、取消單與退貨回補，避免庫存越補越亂",
    ],
    outcomes: ["降低超賣風險", "庫存數據更可信", "客服回應更快"],
    icon: "ri-stack-line",
    image: "/ecommerce-flow-3-multi-channel.png",
    accent: "sky",
    tag: "Step 3",
  },
  {
    id: "pick-pack",
    label: "揀貨、包裝",
    short: "把人手經驗，變成系統化 SOP",
    title: "用掃碼與任務流程，穩定日常揀貨與包裝",
    description:
      "AUTOCHAIN 把派單、揀貨路徑、掃碼核對與包裝步驟串成同一套 SOP，讓倉務團隊即使面對高單量，也能按一致流程完成日常作業。",
    bullets: [
      "支援 batch、wave、zone 等揀貨任務策略",
      "把任務分配、掃碼確認與包裝步驟放到同一工作流程",
      "新同事更容易跟流程操作，減少依賴個別老手經驗",
    ],
    outcomes: ["揀貨節奏更穩定", "包裝步驟更一致", "新人更快上手"],
    icon: "ri-checkbox-circle-line",
    image: "/ecommerce-flow-4-pick-pack-alt.png",
    accent: "rose",
    tag: "Step 4",
  },
  {
    id: "shipping",
    label: "出貨與快遞整合",
    short: "waybill、追蹤資料與平台狀態自動更新",
    title: "倉庫完成出貨，網店與客戶同時收到更新",
    description:
      "當包裹貼上運單並出貨，系統會把快遞公司、tracking number 與狀態更新回網店。這一步做好，客服查件、人手輸入、重覆通知都可以大幅減少。",
    bullets: [
      "對接快遞 API，自動生成 waybill 與出貨紀錄",
      "同步回寫 Shopify 訂單狀態與追蹤連結",
      "支援批量出貨、例外件處理與出貨後通知",
    ],
    outcomes: ["客服查件更少", "出貨更快", "客戶體驗更完整"],
    icon: "ri-truck-line",
    image: "/ecommerce-flow-5-ship-update.png",
    accent: "violet",
    tag: "Step 5",
  },
  {
    id: "shipment-validation",
    label: "出貨前驗證",
    short: "最後核對平台資料一致，才正式放行出貨",
    title: "在最後一步再核對一次，避免用舊資料完成出貨",
    description:
      "當倉務完成揀貨與包裝後，AUTOCHAIN 會在出貨前再次核對平台訂單資料，確保取消單、地址更新或訂單變更不會帶到最後出貨環節。",
    bullets: [
      "執貨完成前核對取消單與訂單變更",
      "包裝完成前核對最新地址更新",
      "確認出貨前最後同步平台資料再放行",
    ],
    outcomes: ["避免取消單出貨", "避免舊地址出貨", "最終資料一致"],
    icon: "ri-shield-check-line",
    image: "/ecommerce-flow-4-pick-pack-alt.png",
    accent: "teal",
    tag: "Step 6",
  },
  {
    id: "visibility",
    label: "報表與營運可視化",
    short: "管理層唔只睇到庫存，仲睇到效率",
    title: "由倉務現場延伸到管理層決策",
    description:
      "除了操作層，系統亦提供儀表板、任務狀態、庫存預警與作業表現分析，讓營運主管知道問題出現在哪一步，而不是月底才發現。",
    bullets: [
      "追蹤當日入庫、待揀、待出貨、異常件與工時狀態",
      "按 SKU、渠道、波次與人員表現做分析",
      "讓倉務主管與管理層用同一份數據對話",
    ],
    outcomes: ["異常更早發現", "排班更有根據", "決策更快"],
    icon: "ri-bar-chart-grouped-line",
    image: "/dashboard.png",
    accent: "slate",
    tag: "Operations",
  },
];

const flowSteps = [
  {
    step: "01",
    title: "產品同步",
    eyebrow: "商品主檔一致",
    description:
      "接通電商平台後，SKU、圖片、變體與銷售渠道資料同步進入 WMS，先把資料底盤整理好。",
  },
  {
    step: "02",
    title: "入庫分配",
    eyebrow: "渠道可售數量設定",
    description:
      "貨品入庫後即按規則分配渠道庫存，同時建立貨位、批次與可售量基準。",
  },
  {
    step: "03",
    title: "庫存聯動",
    eyebrow: "任何渠道變動即時同步",
    description:
      "某平台出單或取消單，其他平台庫存會即時更新，避免管理層與前線看到兩套數字。",
  },
  {
    step: "04",
    title: "揀貨包裝",
    eyebrow: "按任務、掃碼與 SOP 執行",
    description:
      "系統自動派任務，配合掃碼與標準包裝流程，讓高單量情況下的日常作業仍然保持穩定與可追溯。",
  },
  {
    step: "05",
    title: "出貨回寫",
    eyebrow: "物流資訊自動返回網店",
    description:
      "完成出貨後，tracking 與訂單狀態自動更新回平台，客戶與客服同步收到最新進度。",
  },
  {
    step: "06",
    title: "出貨驗證",
    eyebrow: "最後核對平台資料才放行",
    description:
      "在執貨完成、包裝完成與確認出貨前，系統會逐步核對平台資料，避免取消單、地址變更或資料更新被帶入最後出貨環節。",
  },
] as const;

const proofStats = [
  { value: "6 步", label: "完整履行流程" },
  { value: "99.9%", label: "掃碼核驗準確率" },
  { value: "多平台", label: "支援電商渠道同步" },
  { value: "即時", label: "庫存與出貨狀態回寫" },
];

const fitScenarios = [
  {
    title: "多平台電商",
    text: "Shopify、HKTVmall、SHOPLINE、自家網店同時營運，最怕庫存不同步。",
    icon: "ri-store-2-line",
  },
  {
    title: "高 SKU 倉庫",
    text: "商品款式多、變體多、旺季單量波動大，靠 Excel 已經開始失控。",
    icon: "ri-price-tag-3-line",
  },
  {
    title: "需要標準化作業",
    text: "倉務依賴老手經驗，新人難培訓，出錯時又難追責與重整流程。",
    icon: "ri-task-line",
  },
] as const;

const interfaces = [
  {
    title: "倉庫儀表板",
    image: "/dashboard-autochain.png",
    description: "即時掌握待處理訂單、出貨進度、異常件與人手負荷。",
  },
  {
    title: "庫存管理界面",
    image: "/inventory-autochain.png",
    description: "按 SKU、貨位、批次與渠道查庫存，讓查數不再靠人問人。",
  },
  {
    title: "揀貨任務分配",
    image: "/sorting-autochain.png",
    description: "把揀貨任務、優先次序與覆核狀態放到同一個工作畫面。",
  },
] as const;

const partners = [
  { name: "Shopify", logo: "/partner-shopify.png", className: "h-12 md:h-16" },
  { name: "SHOPLINE", logo: "/partner-shopline.png", className: "h-6 md:h-10" },
  { name: "HKTVmall", logo: "/partner-hktvmall.png", className: "h-8 md:h-12" },
  {
    name: "WooCommerce",
    logo: "/partner-woocommerce.png",
    className: "h-12 md:h-16",
  },
  { name: "Amazon", logo: "/partner-amazon.png", className: "h-12 md:h-16" },
  { name: "Tmall", logo: "/partner-tmall.png", className: "h-16 md:h-18" },
] as const;

export default function EcommerceWMS() {
  const [showDemo, setShowDemo] = useState(false);
  const [activeModule, setActiveModule] = useState(0);
  const [activeFlow, setActiveFlow] = useState(0);
  const { t } = useTranslation();

  const currentModule = ecommerceModules[activeModule];
  const currentFlow = flowSteps[activeFlow];
  const color = moduleColors[currentModule.accent];

  const openWhatsApp = (zhMessage: string, enMessage: string) => {
    const message = encodeURIComponent(
      t("nav.language") === "中" ? zhMessage : enMessage,
    );

    window.open(`https://wa.me/85294252903?text=${message}`, "_blank");
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="電商物流 WMS 系統供應商 (Vendor) | 網店倉儲管理 | AUTOCHAIN"
        description="AUTOCHAIN 電商物流 WMS 以完整履行流程為核心，涵蓋商品同步、入庫分配、多平台庫存聯動、揀貨包裝與出貨回寫，協助香港網店與電商提升出貨效率與庫存準確度。"
        keywords="電商WMS, WMS Vendor, WMS供應商, 網店倉儲系統, 網店物流, Shopify WMS, HKTVmall WMS, 多平台庫存同步, 電商倉庫管理, 香港 WMS"
        ogImage="https://autochain.com.hk/wms-og-image.png"
        canonicalUrl="/wms/ecommerce"
      />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            name: "AUTOCHAIN 電商物流 WMS 系統",
            applicationCategory: "BusinessApplication",
            operatingSystem: "Web Browser",
            description:
              "專為電商行業設計的 WMS 倉庫管理系統，支援商品同步、入庫分配、多平台庫存聯動、揀貨包裝與出貨回寫。",
            url: "https://autochain.com.hk/wms/ecommerce",
            aggregateRating: {
              "@type": "AggregateRating",
              ratingValue: "4.9",
              ratingCount: "18",
            },
            offers: {
              "@type": "Offer",
              price: "0",
              priceCurrency: "HKD",
              description: "免費演示",
            },
            provider: {
              "@type": "Organization",
              name: "AUTOCHAIN TECHNOLOGY",
            },
            featureList: ecommerceModules.map((module) => module.label),
          }),
        }}
      />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              {
                "@type": "ListItem",
                position: 1,
                name: "首頁",
                item: "https://autochain.com.hk/",
              },
              {
                "@type": "ListItem",
                position: 2,
                name: "WMS 倉庫管理系統",
                item: "https://autochain.com.hk/wms/ecommerce",
              },
              {
                "@type": "ListItem",
                position: 3,
                name: "電商物流 WMS",
                item: "https://autochain.com.hk/wms/ecommerce",
              },
            ],
          }),
        }}
      />

      <Header />

      <section className="relative overflow-hidden bg-slate-950">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,_rgba(45,212,191,0.28),_transparent_32%),radial-gradient(circle_at_80%_20%,_rgba(251,191,36,0.22),_transparent_28%),linear-gradient(135deg,_rgba(15,23,42,0.96),_rgba(15,23,42,0.82))]" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-28 pb-20 lg:pt-36 lg:pb-28">
          <div className="grid grid-cols-1 lg:grid-cols-[1.15fr_0.85fr] gap-12 items-center">
            <div className="text-white">
              <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-2 text-sm text-slate-200">
                <i className="ri-flashlight-line text-teal-300"></i>
                專為香港電商履行而設
              </div>
              <h1 className="mt-6 text-4xl md:text-6xl font-bold leading-tight">
                電商物流 WMS，
                <span className="block text-teal-300">
                  唔只係管理倉庫，而係管理整條出貨流程
                </span>
              </h1>
              <p className="mt-6 max-w-3xl text-lg md:text-xl leading-8 text-slate-200">
                AUTOCHAIN
                將商品同步、庫存聯動、揀貨包裝與出貨回寫整合成同一套電商履行流程，
                幫助品牌以更穩定的方式處理多平台訂單、減少出錯，並提升整體出貨效率。
              </p>

              <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-3">
                {proofStats.map((stat) => (
                  <div
                    key={stat.label}
                    className="rounded-2xl border border-white/10 bg-white/5 px-4 py-4 backdrop-blur"
                  >
                    <div className="text-2xl font-bold text-white">
                      {stat.value}
                    </div>
                    <div className="mt-1 text-sm text-slate-300">
                      {stat.label}
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-8 flex flex-col sm:flex-row gap-4">
                <Button
                  variant="primary"
                  size="lg"
                  className="text-lg px-8 py-4"
                  onClick={() =>
                    openWhatsApp(
                      "您好！我想了解 AUTOCHAIN 電商物流 WMS 的完整功能介紹，希望預約演示。",
                      "Hello! I would like to learn more about AUTOCHAIN's e-commerce WMS and schedule a demo.",
                    )
                  }
                >
                  預約免費演示
                </Button>
                <Button
                  variant="outline"
                  size="lg"
                  className="text-lg px-8 py-4 bg-white/5 border-white text-white hover:bg-white hover:text-slate-950"
                  onClick={() =>
                    openWhatsApp(
                      "您好！我想索取 AUTOCHAIN 電商物流 WMS 產品手冊與方案資料。",
                      "Hello! I would like to receive AUTOCHAIN's e-commerce WMS brochure and solution details.",
                    )
                  }
                >
                  索取產品手冊
                </Button>
              </div>
            </div>

            <div className="relative">
              <div className="absolute -left-6 top-8 hidden lg:block h-36 w-36 rounded-full bg-teal-400/20 blur-3xl" />
              <div className="relative rounded-[32px] border border-white/10 bg-white p-4 shadow-2xl shadow-black/30">
                <div className="rounded-[24px] bg-slate-100 p-3">
                  <img
                    src="/ecommerce-hero-visual.png"
                    alt="AUTOCHAIN WMS dashboard"
                    className="w-full rounded-[18px] border border-slate-200 object-cover"
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-4">
                  <div className="rounded-2xl bg-slate-950 px-5 py-4 text-white">
                    <div className="text-sm text-slate-300">核心營運挑戰</div>
                    <div className="mt-2 text-lg font-semibold">
                      減少超賣、錯貨與延遲出貨
                    </div>
                  </div>
                  <div className="rounded-2xl bg-teal-50 px-5 py-4">
                    <div className="text-sm text-slate-500">營運目標</div>
                    <div className="mt-2 text-lg font-semibold text-slate-900">
                      建立穩定一致的電商履行流程
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-slate-50 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="inline-flex rounded-full bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-sm">
              核心功能總覽
            </span>
            <h2 className="mt-5 text-3xl md:text-4xl font-bold text-slate-900">
              由核心模組出發，
              <span className="text-teal-700">建立完整電商履行能力</span>
            </h2>
            <p className="mt-4 text-lg text-slate-600 leading-8">
              由商品同步、入庫分配、庫存聯動、揀貨包裝到出貨回寫，
              每個模組都對應電商團隊最常見的營運痛點，協助你建立更穩定、更準確的履行流程。
            </p>
          </div>

          <div className="mt-12 grid grid-cols-1 xl:grid-cols-[420px_1fr] gap-8 items-start">
            <div className="space-y-4">
              {ecommerceModules.map((module, index) => {
                const moduleColor = moduleColors[module.accent];
                const isActive = index === activeModule;

                return (
                  <button
                    key={module.id}
                    type="button"
                    onClick={() => setActiveModule(index)}
                    className={`w-full rounded-3xl border bg-white p-5 text-left transition-all duration-200 ${
                      isActive
                        ? `${moduleColor.border} ring-2 ${moduleColor.ring} shadow-lg`
                        : `border-slate-200 ${moduleColor.button}`
                    }`}
                  >
                    <div className="flex items-start gap-4">
                      <div
                        className={`flex h-12 w-12 items-center justify-center rounded-2xl text-xl ${
                          isActive
                            ? moduleColor.chip
                            : "bg-slate-100 text-slate-600"
                        }`}
                      >
                        <i className={module.icon}></i>
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between gap-4">
                          <div className="text-lg font-semibold text-slate-900">
                            {module.label}
                          </div>
                          <span
                            className={`shrink-0 rounded-full px-3 py-1 text-xs font-semibold ${
                              isActive
                                ? moduleColor.chip
                                : "bg-slate-100 text-slate-600"
                            }`}
                          >
                            {module.tag}
                          </span>
                        </div>
                        <p className="mt-2 text-sm leading-6 text-slate-600">
                          {module.short}
                        </p>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>

            <div className="rounded-[32px] border border-slate-200 bg-white shadow-xl shadow-slate-200/60 overflow-hidden">
              <div className={`bg-gradient-to-r ${color.panel} p-8 text-white`}>
                <div className="flex flex-wrap items-center gap-3">
                  <span className="rounded-full bg-white/15 px-4 py-2 text-sm font-medium backdrop-blur">
                    {currentModule.tag}
                  </span>
                  <span className="rounded-full bg-white/10 px-4 py-2 text-sm backdrop-blur">
                    {currentModule.label}
                  </span>
                </div>
                <h3 className="mt-5 text-3xl font-bold leading-tight">
                  {currentModule.title}
                </h3>
                <p className="mt-4 max-w-3xl text-lg leading-8 text-white/90">
                  {currentModule.description}
                </p>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-[0.95fr_1.05fr] gap-0">
                <div className="p-8 border-b lg:border-b-0 lg:border-r border-slate-200">
                  <h4 className="text-sm font-semibold uppercase tracking-[0.18em] text-slate-500">
                    這個模組會做什麼
                  </h4>
                  <div className="mt-5 space-y-4">
                    {currentModule.bullets.map((bullet) => (
                      <div
                        key={bullet}
                        className={`flex items-start gap-3 rounded-2xl ${color.soft} p-4`}
                      >
                        <i
                          className={`ri-check-double-line mt-1 text-lg ${color.text}`}
                        ></i>
                        <p className="text-slate-700 leading-7">{bullet}</p>
                      </div>
                    ))}
                  </div>

                  <h4 className="mt-8 text-sm font-semibold uppercase tracking-[0.18em] text-slate-500">
                    預期成果
                  </h4>
                  <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
                    {currentModule.outcomes.map((outcome) => (
                      <div
                        key={outcome}
                        className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4 text-center text-sm font-semibold text-slate-800"
                      >
                        {outcome}
                      </div>
                    ))}
                  </div>

                  <div className="mt-8 flex flex-col sm:flex-row gap-4">
                    <Button
                      variant="primary"
                      onClick={() =>
                        openWhatsApp(
                          `您好！我想重點了解 AUTOCHAIN 電商物流 WMS 的「${currentModule.label}」模組。`,
                          `Hello! I would like to learn more about the "${currentModule.label}" module in AUTOCHAIN's e-commerce WMS.`,
                        )
                      }
                    >
                      了解這個模組
                    </Button>
                    <Button
                      variant="outline"
                      className="border-slate-300 text-slate-700 hover:bg-slate-100 hover:text-slate-900"
                      onClick={() => setShowDemo(true)}
                    >
                      預約演示
                    </Button>
                  </div>
                </div>

                <div className="p-8">
                  <div className="rounded-[28px] border border-slate-200 bg-slate-50 p-4">
                    <img
                      src={currentModule.image}
                      alt={currentModule.label}
                      className="w-full rounded-[20px] object-cover shadow-lg"
                    />
                  </div>
                  <div className="mt-5 rounded-3xl border border-slate-200 bg-white p-5">
                    <div className="text-sm font-semibold uppercase tracking-[0.18em] text-slate-500">
                      適合這類電商團隊
                    </div>
                    <p className="mt-3 text-slate-700 leading-7">
                      正在處理多平台訂單、SKU 變體多、需要穩定倉務 SOP
                      的香港電商與零售品牌，尤其適合已經感受到 Excel
                      或人手操作開始失控的團隊。
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-[0.85fr_1.15fr] gap-12 items-start">
            <div>
              <span className="inline-flex rounded-full bg-teal-100 px-4 py-2 text-sm font-medium text-teal-700">
                履行流程
              </span>
              <h2 className="mt-5 text-3xl md:text-4xl font-bold text-slate-900 leading-tight">
                由接單到出貨，
                <span className="block text-slate-600">
                  建立清晰一致的履行流程
                </span>
              </h2>
              <p className="mt-4 text-lg text-slate-600 leading-8">
                將各個作業步驟整合到同一套系統內，減少跨部門溝通落差，並提升整體履行效率與準確度。
              </p>
            </div>

            <div>
              <div className="grid grid-cols-2 lg:grid-cols-6 gap-3">
                {flowSteps.map((flow, index) => {
                  const isActive = index === activeFlow;
                  return (
                    <button
                      key={flow.step}
                      type="button"
                      onClick={() => setActiveFlow(index)}
                      className={`rounded-3xl border px-4 py-4 text-left transition-all ${
                        isActive
                          ? "border-slate-900 bg-slate-900 text-white shadow-lg"
                          : "border-slate-200 bg-white hover:border-slate-400"
                      }`}
                    >
                      <div className="text-sm font-semibold">{flow.step}</div>
                      <div className="mt-2 text-base font-semibold">
                        {flow.title}
                      </div>
                    </button>
                  );
                })}
              </div>

              <div className="mt-6 rounded-[32px] border border-slate-200 bg-slate-50 p-8">
                <div className="text-sm font-semibold uppercase tracking-[0.18em] text-slate-500">
                  {currentFlow.eyebrow}
                </div>
                <h3 className="mt-3 text-3xl font-bold text-slate-900">
                  {currentFlow.step}. {currentFlow.title}
                </h3>
                <p className="mt-4 text-lg leading-8 text-slate-700">
                  {currentFlow.description}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-slate-950 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <span className="inline-flex rounded-full border border-white/10 bg-white/5 px-4 py-2 text-sm font-medium text-slate-200">
              適用場景
            </span>
            <h2 className="mt-5 text-3xl md:text-4xl font-bold text-white">
              適合正在擴展中的電商團隊，
              <span className="text-teal-300">
                尤其是已經開始感受到履行壓力的品牌
              </span>
            </h2>
          </div>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
            {fitScenarios.map((scenario) => (
              <div
                key={scenario.title}
                className="rounded-[28px] border border-white/10 bg-white/5 p-7 text-white"
              >
                <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white/10 text-2xl text-teal-300">
                  <i className={scenario.icon}></i>
                </div>
                <h3 className="mt-5 text-xl font-semibold">{scenario.title}</h3>
                <p className="mt-3 text-slate-300 leading-7">{scenario.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
            <div className="max-w-3xl">
              <span className="inline-flex rounded-full bg-slate-100 px-4 py-2 text-sm font-medium text-slate-700">
                介面預覽
              </span>
              <h2 className="mt-5 text-3xl md:text-4xl font-bold text-slate-900">
                直觀介面設計，
                <span className="text-teal-700">讓團隊更快上手日常操作</span>
              </h2>
            </div>
            <p className="max-w-2xl text-lg leading-8 text-slate-600">
              由倉庫儀表板、庫存管理到揀貨任務分配，系統介面以實際營運需要為核心，協助團隊更有效執行日常作業。
            </p>
          </div>

          <div className="mt-12 grid grid-cols-1 lg:grid-cols-3 gap-6">
            {interfaces.map((item) => (
              <div
                key={item.title}
                className="rounded-[30px] border border-slate-200 bg-slate-50 p-5 shadow-sm"
              >
                <img
                  src={item.image}
                  alt={item.title}
                  className="w-full rounded-[22px] border border-slate-200 bg-white object-cover shadow-lg"
                />
                <h3 className="mt-5 text-xl font-semibold text-slate-900">
                  {item.title}
                </h3>
                <p className="mt-3 text-slate-600 leading-7">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-slate-50 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 xl:grid-cols-[1.1fr_0.9fr] gap-8">
            <div className="space-y-8">
              <div className="rounded-[34px] bg-white p-8 shadow-xl shadow-slate-200/60">
                <div className="flex items-center gap-4">
                  <img
                    src="/casetify-logo.png"
                    alt="Casetify"
                    className="h-14 w-14 rounded-2xl object-contain"
                  />
                  <div>
                    <div className="text-xl font-semibold text-slate-900">
                      Casetify
                    </div>
                    <div className="text-slate-500">
                      高 SKU、高波動電商履行場景
                    </div>
                  </div>
                </div>
                <p className="mt-6 text-lg leading-8 text-slate-700">
                  當訂單波動大、SKU 結構複雜、旺季壓力高時，WMS
                  最重要的是讓團隊穩定處理高峰期訂單，同時守住出貨準確度與跨平台庫存一致性。
                </p>
                <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="rounded-2xl bg-slate-50 p-5 text-center">
                    <div className="text-3xl font-bold text-teal-700">55%</div>
                    <div className="mt-2 text-sm text-slate-600">效率提升</div>
                  </div>
                  <div className="rounded-2xl bg-slate-50 p-5 text-center">
                    <div className="text-3xl font-bold text-sky-700">
                      99.95%
                    </div>
                    <div className="mt-2 text-sm text-slate-600">準確率</div>
                  </div>
                  <div className="rounded-2xl bg-slate-50 p-5 text-center">
                    <div className="text-3xl font-bold text-amber-600">
                      50萬+
                    </div>
                    <div className="mt-2 text-sm text-slate-600">
                      日處理訂單
                    </div>
                  </div>
                  <div className="rounded-2xl bg-slate-50 p-5 text-center">
                    <div className="text-3xl font-bold text-rose-600">24/7</div>
                    <div className="mt-2 text-sm text-slate-600">
                      無間斷運作
                    </div>
                  </div>
                </div>
              </div>

              <div className="rounded-[34px] bg-white p-8 shadow-xl shadow-slate-200/60">
                <div className="flex items-center gap-4">
                  <img
                    src="/shiro-logo.png"
                    alt="SHIRO Logo"
                    className="h-14 w-14 rounded-2xl object-contain"
                  />
                  <div>
                    <div className="text-xl font-semibold text-slate-900">
                      SHIRO
                    </div>
                    <div className="text-slate-500">日本人氣美妝護膚品牌</div>
                  </div>
                </div>
                <p className="mt-6 text-lg leading-8 text-slate-700">
                  SHIRO 進軍香港市場時，面對網店與實體店鋪庫存共存的挑戰，
                  AUTOCHAIN WMS
                  協助其香港倉庫整合網店、門市與出貨流程，讓庫存同步、退貨處理與物流安排可以更穩定地運作。
                </p>
                <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="rounded-2xl bg-slate-50 p-5 text-center">
                    <div className="text-3xl font-bold text-violet-700">
                      即時
                    </div>
                    <div className="mt-2 text-sm text-slate-600">
                      全渠道庫存同步
                    </div>
                  </div>
                  <div className="rounded-2xl bg-slate-50 p-5 text-center">
                    <div className="text-3xl font-bold text-rose-600">40%</div>
                    <div className="mt-2 text-sm text-slate-600">
                      出貨效率提升
                    </div>
                  </div>
                  <div className="rounded-2xl bg-slate-50 p-5 text-center">
                    <div className="text-3xl font-bold text-sky-700">
                      自動化
                    </div>
                    <div className="mt-2 text-sm text-slate-600">
                      退貨回補流程
                    </div>
                  </div>
                  <div className="rounded-2xl bg-slate-50 p-5 text-center">
                    <div className="text-3xl font-bold text-amber-600">
                      優化
                    </div>
                    <div className="mt-2 text-sm text-slate-600">
                      庫存周轉管理
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="rounded-[34px] border border-slate-200 bg-white p-8 shadow-xl shadow-slate-200/60">
              <div className="text-sm font-semibold uppercase tracking-[0.18em] text-slate-500">
                平台整合
              </div>
              <h3 className="mt-3 text-3xl font-bold text-slate-900">
                無縫連接主流電商平台與銷售渠道
              </h3>
              <p className="mt-4 text-slate-600 leading-8">
                讓商品、訂單與庫存資料集中到同一套履行流程內，減少重覆輸入與渠道之間的資料落差，提升整體營運效率。
              </p>

              <div className="mt-8 grid grid-cols-2 gap-4">
                {partners.map((partner) => (
                  <div
                    key={partner.name}
                    className="flex items-center justify-center rounded-2xl border border-slate-200 bg-slate-50 px-4 py-6"
                  >
                    <img
                      src={partner.logo}
                      alt={partner.name}
                      className={`${partner.className} object-contain`}
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-gradient-to-r from-teal-600 to-cyan-600 py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
          <h2 className="text-4xl font-bold">
            讓 AUTOCHAIN 電商物流 WMS
            <span className="block">幫你建立更穩定的出貨流程</span>
          </h2>
          <p className="mt-6 text-xl leading-8 text-white/90">
            無論你正處理多平台訂單、高 SKU
            倉務管理，還是希望在旺季保持出貨穩定，
            我們都可以按你的業務模式提供合適的履行方案與系統示範。
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              variant="outline"
              size="lg"
              className="bg-white text-teal-700 border-white hover:bg-teal-50 px-8 py-4 text-lg"
              onClick={() => setShowDemo(true)}
            >
              預約免費演示
            </Button>
            <Button
              variant="secondary"
              size="lg"
              className="px-8 py-4 text-lg bg-slate-950 hover:bg-slate-900"
              onClick={() =>
                openWhatsApp(
                  "您好！我想了解 AUTOCHAIN 電商物流 WMS 如何配合我們的電商履行流程。",
                  "Hello! I would like to learn how AUTOCHAIN's e-commerce WMS can support our fulfillment workflow.",
                )
              }
            >
              WhatsApp 聯絡我們
            </Button>
          </div>
        </div>
      </section>

      {showDemo && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 p-4">
          <div className="w-full max-w-md rounded-[28px] bg-white p-8 shadow-2xl">
            <div className="text-center">
              <h3 className="text-2xl font-bold text-slate-900">
                預約免費演示
              </h3>
              <p className="mt-2 text-slate-600">
                填寫以下資訊，我們將盡快與您聯繫
              </p>
            </div>

            <form className="mt-6 space-y-4">
              <input
                type="text"
                placeholder="姓名"
                className="w-full rounded-2xl border border-slate-300 px-4 py-3 text-sm focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-500/20"
              />
              <input
                type="text"
                placeholder="公司名稱"
                className="w-full rounded-2xl border border-slate-300 px-4 py-3 text-sm focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-500/20"
              />
              <input
                type="email"
                placeholder="電子郵件"
                className="w-full rounded-2xl border border-slate-300 px-4 py-3 text-sm focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-500/20"
              />

              <div className="flex gap-3 pt-3">
                <Button variant="primary" className="flex-1">
                  提交申請
                </Button>
                <Button
                  variant="outline"
                  className="border-slate-300 text-slate-700 hover:bg-slate-100 hover:text-slate-900"
                  onClick={() => setShowDemo(false)}
                >
                  取消
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}
